import PropTypes from 'prop-types';
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  Icon,
  ModalOverlay,
  orange,
  PlainButton,
  tokens,
  useIsMobile,
} from 'components/v3';
import { FONT_SYSTEM } from 'components/v3/tokens';
import { brand } from '../shared/brand';
import { phone } from './palette';
import PhoneFrame from './PhoneFrame';
import PreviewForm from './PreviewForm';
import usePreviewTheme from './usePreviewTheme';

// In-app preview (build plan §5.7, T32): what a racer sees filling this form
// in the app, rendered from the builder's unsaved working copy. Fully
// interactive, with the server's validation. Desktop: the fill inside an
// iPhone-proportion frame next to a short explainer, with an orange close
// button on the modal corner. Phones: the fill full screen, no frame.
//
// Props (the contract A1 seeded; the builder renders this):
//   open, onClose,
//   form: { name, description, account: { name }, show_progress },
//   definition: { schema: 1, fields: [...] },
//   assetUrls: { [assetId]: url }

// Stage holds the explainer, the phone and the close button.
const Stage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 28px;
  font-family: ${FONT_SYSTEM};

  ${(p) =>
    p.$mobile &&
    `
    flex: 1;
    align-items: stretch;
    width: 100%;
    min-width: 0;
  `}
`;

const CloseButton = styled(PlainButton)`
  position: absolute;
  z-index: 10;
  top: ${(p) =>
    p.$mobile ? 'max(12px, calc(env(safe-area-inset-top) + 4px))' : '-14px'};
  right: ${(p) => (p.$mobile ? '12px' : '-14px')};
  width: ${(p) => (p.$mobile ? 40 : 42)}px;
  height: ${(p) => (p.$mobile ? 40 : 42)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(p) => tokens(p.theme).primary};
  border: 2px solid ${(p) => tokens(p.theme).card};
  color: white;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
  transition: transform 150ms ease;

  &:hover {
    transform: scale(1.06);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const Info = styled.aside`
  width: 300px;
  box-sizing: border-box;
  padding: 24px;
  border-radius: 14px;
  background: ${(p) => tokens(p.theme).card};
  border: 1px solid ${(p) => tokens(p.theme).border};
  box-shadow: ${(p) => tokens(p.theme).shadowPop};
  color: ${(p) => tokens(p.theme).text};

  @media (max-width: 999px) {
    display: none;
  }

  h2 {
    margin: 0 0 10px;
    font-size: 20px;
    font-weight: 700;
  }

  p {
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.55;
    color: ${(p) => tokens(p.theme).sub};
  }
`;

const InfoTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${(p) => tokens(p.theme).primary};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 14px;

  svg {
    color: ${(p) => tokens(p.theme).primary};
  }
`;

// The in-phone appearance button (design: .m-theme-toggle): a square icon
// button showing where it goes — a moon in light, a sun in dark.
const ThemeButton = styled(PlainButton)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 9px;
  background: ${(p) => phone(p.theme).surface};
  border: 1px solid ${(p) => phone(p.theme).border};
  color: ${(p) => phone(p.theme).text2};
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease;

  &:hover {
    background: ${orange(0.14)};
    border-color: ${orange(1)};
    color: ${orange(1)};
  }

  &:active {
    transform: scale(0.92);
  }
`;

const InfoPanel = () => (
  <Info>
    <InfoTag>
      <Icon name="smartphone" size={14} />
      In-app preview
    </InfoTag>
    <h2>How this form gets filled out</h2>
    <p>
      This is the embedded form inside the {brand().name} app. Try it — it
      validates as you go.
    </p>
    <InfoRow>
      <Icon name="list" size={18} />
      Every field full-width, top to bottom
    </InfoRow>
    <InfoRow>
      <Icon name="circle-check" size={18} />
      Submit surfaces a missing-fields summary
    </InfoRow>
    <InfoRow>
      <Icon name="info" size={18} />
      Shows unsaved edits. Nothing is saved.
    </InfoRow>
  </Info>
);

const MOBILE_PANEL = { flex: 1, minWidth: 0, display: 'flex' };

const PreviewModal = ({ open, onClose, form, definition, assetUrls }) => {
  const isMobile = useIsMobile();
  const { theme, isLight, toggleTheme } = usePreviewTheme();

  const themeControl = (
    <ThemeButton
      aria-label="Light appearance"
      aria-pressed={isLight}
      title={isLight ? 'Switch to dark' : 'Switch to light'}
      onClick={toggleTheme}
    >
      <Icon name={isLight ? 'moon' : 'sun'} size={17} />
    </ThemeButton>
  );

  const fill = (
    <PreviewForm
      form={form || {}}
      definition={definition || undefined}
      assetUrls={assetUrls || {}}
      framed={!isMobile}
      headerAction={themeControl}
    />
  );

  return (
    <ModalOverlay
      open={open}
      onClose={onClose}
      label="Form preview"
      placement={isMobile ? 'full' : 'center'}
      panelStyle={isMobile ? MOBILE_PANEL : undefined}
    >
      <Stage $mobile={isMobile}>
        <CloseButton
          $mobile={isMobile}
          aria-label="Close preview"
          title="Close preview"
          onClick={onClose}
        >
          <Icon name="x" size={20} />
        </CloseButton>
        {isMobile ? null : <InfoPanel />}
        <ThemeProvider theme={(outer) => ({ ...(outer || {}), name: theme })}>
          {isMobile ? fill : <PhoneFrame>{fill}</PhoneFrame>}
        </ThemeProvider>
      </Stage>
    </ModalOverlay>
  );
};

PreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  form: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    account: PropTypes.shape({ name: PropTypes.string }),
    show_progress: PropTypes.bool,
  }),
  definition: PropTypes.shape({
    schema: PropTypes.number,
    fields: PropTypes.array,
  }),
  assetUrls: PropTypes.object,
};

PreviewModal.defaultProps = {
  form: {},
  definition: undefined,
  assetUrls: {},
};

export default PreviewModal;
